import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import type { UINotification, TaskNotification, ChatNotification } from '../types'
import { API_URL, getAuthHeaders } from '../api'


export function useTaskNotifications() {
  const { token } = useAuth()
  const [notifications, setNotifications] = useState<UINotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [pushSupported, setPushSupported] = useState(false)
  const [pushSubscribed, setPushSubscribed] = useState(false)

  const authHeaders = getAuthHeaders()

  const loadNotifications = useCallback(async () => {
    if (!token) return
    try {
      const [taskRes, chatRes] = await Promise.all([
        fetch(`${API_URL}/api/task-notifications?limit=30`, { headers: authHeaders }),
        fetch(`${API_URL}/api/chat-notifications?limit=30`, { headers: authHeaders }),
      ])
      const taskData = taskRes.ok ? await taskRes.json() : { notifications: [] }
      const chatData = chatRes.ok ? await chatRes.json() : { notifications: [] }
      const taskNotifs = (taskData.notifications || []).map((n: TaskNotification) => ({ ...n, _kind: 'task' as const }))
      const chatNotifs = (chatData.notifications || []).map((n: ChatNotification) => ({ ...n, _kind: 'chat' as const }))
      const all = [...taskNotifs, ...chatNotifs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 50)
      setNotifications(all)
    } catch { /* ignore */ }
  }, [token])

  const loadUnreadCount = useCallback(async () => {
    if (!token) return
    try {
      const [taskRes, chatRes] = await Promise.all([
        fetch(`${API_URL}/api/task-notifications/unread`, { headers: authHeaders }),
        fetch(`${API_URL}/api/chat-notifications/unread`, { headers: authHeaders }),
      ])
      const taskData = taskRes.ok ? await taskRes.json() : { count: 0 }
      const chatData = chatRes.ok ? await chatRes.json() : { count: 0 }
      setUnreadCount((taskData.count || 0) + (chatData.count || 0))
    } catch { /* ignore */ }
  }, [token])

  const markAsRead = useCallback(async (id: string) => {
    const notif = notifications.find(n => n.id === id)
    if (!notif) return
    const endpoint = notif._kind === 'chat' ? 'chat-notifications' : 'task-notifications'
    try {
      await fetch(`${API_URL}/api/${endpoint}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ id }),
      })
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch { /* ignore */ }
  }, [token, notifications])

  const markAllAsRead = useCallback(async () => {
    try {
      await Promise.all([
        fetch(`${API_URL}/api/task-notifications/read-all`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders } }),
        fetch(`${API_URL}/api/chat-notifications/read-all`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders } }),
      ])
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })))
      setUnreadCount(0)
    } catch { /* ignore */ }
  }, [token])

  // Web push subscription
  const subscribePush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushSupported(false)
      return null
    }
    setPushSupported(true)

    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready

      const keyRes = await fetch(`${API_URL}/api/push/subscribe`, { headers: authHeaders })
      if (!keyRes.ok) {
        console.error('Push: failed to get VAPID key:', keyRes.status)
        return null
      }
      const keyData = await keyRes.json()
      const vapidKey = keyData.public_key
      if (!vapidKey) {
        console.error('Push: no VAPID public key in response')
        return null
      }

      const convertedKey = urlBase64ToUint8Array(vapidKey)
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedKey,
        })
      }

      if (!sub) {
        console.error('Push subscription failed: no subscription returned')
        return null
      }

      // Helper: extract keys from subscription. Some browsers (notably
      // Safari/iOS) don't populate sub.keys but DO expose them via
      // PushSubscription.getKey() which returns ArrayBuffers.
      const extractKeys = (subscription: PushSubscription & { keys?: { p256dh?: string; auth?: string } }) => {
        if (subscription.keys && subscription.keys.p256dh && subscription.keys.auth) {
          return { p256dh: subscription.keys.p256dh, auth: subscription.keys.auth }
        }
        // Fallback: use getKey() which returns ArrayBuffer
        try {
          const p256dhBuf = subscription.getKey('p256dh')
          const authBuf = subscription.getKey('auth')
          if (p256dhBuf && authBuf) {
            // ArrayBuffer -> base64url
            const toB64Url = (buf: ArrayBuffer) => {
              const bytes = new Uint8Array(buf)
              let bin = ''
              for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
              return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
            }
            return { p256dh: toB64Url(p256dhBuf), auth: toB64Url(authBuf) }
          }
        } catch (e: unknown) {
          console.warn('Push getKey() fallback failed:', e)
        }
        return null
      }

      let keys = extractKeys(sub)

      // If keys still missing, unsubscribe and retry once
      if (!keys) {
        console.warn('Push subscription missing keys, unsubscribing and retrying...')
        try { await sub.unsubscribe() } catch {}
        // Small delay before retry — some browsers need time to clean up
        await new Promise(r => setTimeout(r, 300))
        try {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedKey,
          })
        } catch (err: unknown) {
          console.error('Push retry failed:', err)
          return null
        }
        keys = sub ? extractKeys(sub) : null
        if (!keys) {
          console.warn('Push subscription still missing keys after retry — skipping backend sync')
          setPushSubscribed(true)
          return sub
        }
      }

      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: {
            p256dh: keys.p256dh,
            auth: keys.auth,
          },
        }),
      })
      setPushSubscribed(true)
      return sub
    } catch (err: unknown) {
      console.error('Push subscription failed:', err)
      return null
    }
  }, [token])

  // Listen for real-time task notifications via global event (from useNotifications WebSocket)
  useEffect(() => {
    const taskHandler = (e: Event) => {
      const msg = (e as CustomEvent).detail
      setNotifications(prev => [{
        id: msg.id,
        task_id: msg.task_id,
        task_title: msg.task_title,
        action: msg.action,
        actor_id: msg.actor_id,
        actor_name: msg.actor_name,
        message: msg.message,
        read_at: null,
        created_at: msg.timestamp,
        _kind: 'task' as const,
      }, ...prev].slice(0, 50))
      setUnreadCount(prev => prev + 1)
    }
    const chatHandler = (e: Event) => {
      const msg = (e as CustomEvent).detail
      setNotifications(prev => [{
        id: msg.id || crypto.randomUUID(),
        channel_id: msg.channel_id,
        channel_name: msg.channel_name,
        sender_id: msg.sender_id,
        sender_name: msg.sender_name,
        content: msg.content,
        mention_type: msg.mention_type,
        message: `${msg.sender_name} mencionou você em #${msg.channel_name}`,
        read_at: null,
        created_at: msg.timestamp,
        _kind: 'chat' as const,
      }, ...prev].slice(0, 50))
      setUnreadCount(prev => prev + 1)
    }
    window.addEventListener('task-notification', taskHandler)
    window.addEventListener('chat-notification', chatHandler)
    return () => {
      window.removeEventListener('task-notification', taskHandler)
      window.removeEventListener('chat-notification', chatHandler)
    }
  }, [])

  // Initial load + auto-subscribe push
  useEffect(() => {
    loadNotifications()
    loadUnreadCount()
    if ('Notification' in window && Notification.permission === 'granted') {
      subscribePush()
    } else if ('serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true)
    }
  }, [loadNotifications, loadUnreadCount, subscribePush])

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    pushSupported,
    pushSubscribed,
    subscribePush,
    loadNotifications,
    loadUnreadCount,
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
